import { TXN_PANEL_DIALOG_CLASS } from "@app/components/txn-table-panel";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { walletCounterpartyPanel_Query } from "@app/queries/walletCounterpartyPanel_Query.graphql";

// The scope a clicked grid cell drills into — cell-scoped so the panel's line list
// sums to exactly the amount that was clicked.
export type CellScope =
  | { kind: "cp-month"; counterparty: string; label: string; month: string }
  | { kind: "cp-year"; counterparty: string; label: string }
  | { kind: "all-month"; label: string; month: string }
  | { kind: "all-year"; label: string };

const linesQuery = graphql`
  query walletCounterpartyPanel_Query(
    $slug: String!
    $counterparty: String
    $month: String
    $year: Int
  ) {
    walletCounterpartyLines(
      slug: $slug
      counterparty: $counterparty
      month: $month
      year: $year
    ) {
      date
      counterparty
      memo
      currency
      amount
    }
  }
`;

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// The variables that scope the line list to exactly the clicked cell. A full-year
// cell passes `year` (no month); a month cell passes `month`. The totals-row cells
// omit `counterparty` so every payee is included.
function variablesFor(scope: CellScope, slug: string, year: number) {
  switch (scope.kind) {
    case "cp-month":
      return { slug, counterparty: scope.counterparty, month: scope.month, year: undefined };
    case "cp-year":
      return { slug, counterparty: scope.counterparty, month: undefined, year };
    case "all-month":
      return { slug, counterparty: undefined, month: scope.month, year: undefined };
    case "all-year":
      return { slug, counterparty: undefined, month: undefined, year };
  }
}

export default function WalletCounterpartyPanel({
  slug,
  year,
  scope,
  onClose,
}: {
  slug: string;
  year: number;
  scope: CellScope;
  onClose: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className={TXN_PANEL_DIALOG_CLASS}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading transactions…</div>}>
            <PanelContent slug={slug} year={year} scope={scope} onClose={onClose} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PanelContent({
  slug,
  year,
  scope,
  onClose,
}: {
  slug: string;
  year: number;
  scope: CellScope;
  onClose: () => void;
}) {
  const data = useLazyLoadQuery<walletCounterpartyPanel_Query>(
    linesQuery,
    variablesFor(scope, slug, year),
    { fetchPolicy: "store-or-network" }
  );
  const lines = data.walletCounterpartyLines;
  const total = lines.reduce((sum, line) => sum + line.amount, 0);
  const showCounterparty = scope.kind === "all-month" || scope.kind === "all-year";

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
        <div>
          <Dialog.Title className="font-semibold text-gray-900">{scope.label}</Dialog.Title>
          <p className="text-xs text-gray-500">
            {lines.length.toLocaleString()} imported line{lines.length === 1 ? "" : "s"} ·{" "}
            {money(total)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        {lines.length === 0 ? (
          <p className="p-6 text-sm text-gray-400">No imported lines in this cell.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-xs text-gray-400 uppercase border-b">
                <th className="px-4 py-2">Date</th>
                {showCounterparty && <th className="px-4 py-2">Counterparty</th>}
                <th className="px-4 py-2">Memo</th>
                <th className="px-4 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={`${line.date}-${line.counterparty}-${i}`} className="border-b last:border-b-0">
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">{line.date ?? "—"}</td>
                  {showCounterparty && (
                    <td className="px-4 py-2 text-gray-800">{line.counterparty}</td>
                  )}
                  <td className="px-4 py-2 text-gray-500">{line.memo ?? "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                    {money(line.amount)}
                    {line.currency && line.currency !== "USD" && (
                      <span className="ml-1 text-xs text-amber-600" title="Original currency of this line">
                        {line.currency}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
